import * as t from 'io-ts';
import _, {isString, isUndefined, map} from 'lodash';

import {parseAccess, RAccess} from './accessModel';
import {parseAnswerFolder, RAnswerFolder} from './answerFolderModel';
import {parseAnswer, RAnswer} from './answerModel';
import {makeApiType, makeDefaultValueType} from './apiTypes';
import {parseCalendarFromCalendarVersion, RCalendar} from './calendarModel';
import {parseCalendarVersion, RCalendarVersion} from './calendarVersionModel';
import {parseChannel, RChannel} from './channelModel';
import {parseCompany, RCompany} from './companyModel';
import {parseContactsFromChannel, RContact} from './contactModel';
import {parseCountersFromBoot, RCounters} from './countersModel';
import {parseCustomField, RCustomField} from './customFieldModel';
import {parseCustomView, RCustomView} from './customViewModel';
import {
  mergeDataGroups,
  parseDataGroupFromConversation,
  parseDataGroupFromLens,
  parseDataGroupFromTag,
  RDataGroup,
} from './dataGroupModel';
import {parseDelegation, RDelegation} from './delegationModel';
import {parseHelpChatConfigModel, RHelpChatConfig} from './helpChatConfigModel';
import {parseInbox, RInbox} from './inboxModel';
import {parseBootIntegrations, RIntegration} from './integrationModel';
import {RJob, tryParseJob} from './jobModel';
import {parseLaunchdarklyConfig, RLaunchdarklyConfig} from './launchdarklyConfigModel';
import {parseLegacyApplication, RLegacyApplication} from './legacyApplicationModel';
import {RLens} from './lensModel';
import {parsePlugin, RPlugin} from './pluginModel';
import {parsePreferences, RPreferences} from './preferencesModel';
import {parsePushConfigModel, parsePusherConfigModel, RCPushConfig, RPushConfig} from './pushConfigModel';
import {parseRole, RRole} from './roleModel';
import {parseSignature, RSignature} from './signatureModel';
import {RTag} from './tagModel';
import {parseTeammate, RTeammate} from './teammateModel';
import {parseTeam, RTeam} from './teamModel';
import {parseUserMetadata, RUserMetadata} from './userMetadataModel';
import {parseVacationResponder, RVacationResponder} from './vacationResponderModel';
import {parseWorkspace, RWorkspace} from './workspaceModel';

/*
 * Runtime type.
 */

export enum AppLanguagesEnum {
  ENGLISH = 'en',
}

export const RPreBoot = t.readonly(
  t.type({
    userId: t.number,
    userIsVisitor: t.boolean,
    companyId: t.number,
    companyUrl: t.string,
    companyRegion: t.union([t.string, t.undefined]),
    companyCell: t.union([t.string, t.undefined]),
    hasBootAccessibleInboxes: t.boolean,
  }),
);

export const RBoot = t.readonly(
  t.type({
    access: RAccess,
    preferences: RPreferences,
    userMetadata: RUserMetadata,
    counters: RCounters,
    company: RCompany,
    userId: t.Integer,
    fetchedAt: t.Integer,
    teams: t.readonlyArray(RTeam),
    teammates: t.readonlyArray(RTeammate),
    roles: t.readonlyArray(RRole),
    jobs: t.readonlyArray(RJob),
    inboxes: makeResourceTable(RInbox),
    channels: makeResourceTable(RChannel),
    answers: makeResourceTable(RAnswer),
    answerFolders: makeResourceTable(RAnswerFolder),
    signatures: makeResourceTable(RSignature),
    receivedDelegations: t.readonlyArray(RDelegation),
    integrations: t.readonlyArray(RIntegration),
    legacyApplications: t.readonlyArray(RLegacyApplication),
    customViews: t.readonlyArray(RCustomView),
    customFields: t.readonlyArray(RCustomField),
    calendarVersions: makeDefaultValueType(t.readonlyArray(RCalendarVersion), []),
    calendars: makeDefaultValueType(t.readonlyArray(RCalendar), []),
    plugins: t.readonlyArray(RPlugin),
    contacts: t.readonlyArray(RContact),
    tags: t.readonlyArray(RTag),
    lenses: t.readonlyArray(RLens),
    pushConfig: RPushConfig,
    chatPushConfig: t.union([t.undefined, RCPushConfig]),
    helpChatConfig: t.union([t.undefined, RHelpChatConfig]),
    host: t.string,
    buildVersion: t.string,
    launchdarkly: RLaunchdarklyConfig,
    isImpersonating: t.boolean,
    hasTrialRestarted: t.boolean,
    allowedDomainForEmbed: t.union([t.undefined, t.string]),
    conversationDataGroup: t.union([t.undefined, RDataGroup]),
    vacationResponders: t.readonlyArray(RVacationResponder),
    workspace: RWorkspace,
  }),
);

export const RTeammatesBootPart = t.readonly(
  t.type({
    fetchedAt: t.number,
    // These array members will be validated by RBoot after we assemble a full boot.
    teammates: t.readonlyArray(t.unknown),
    total: t.number,
    checkpoint: t.union([t.string, t.number, t.null]),
  }),
);

export const RInboxesAndChannelsBootPart = t.readonly(
  t.type({
    fetchedAt: t.number,
    // These array members will be validated by RBoot after we assemble a full boot.
    inboxes: t.readonlyArray(t.unknown),
    channels: t.readonlyArray(t.unknown),
    total: t.number,
    checkpoint: t.union([t.string, t.number, t.null]),
  }),
);

function makeResourceTable<T extends t.Mixed>(type: T) {
  return t.type({
    all: t.readonlyArray(type),
    deletedIds: t.readonlyArray(t.Integer),
  });
}

/*
 * Type.
 */

export interface PreBootModel extends t.TypeOf<typeof RPreBoot> {}
export interface BootModel extends t.TypeOf<typeof RBoot> {}
export interface TeammatesBootPartModel extends t.TypeOf<typeof RTeammatesBootPart> {}
export interface InboxesAndChannelsBootPartModel extends t.TypeOf<typeof RInboxesAndChannelsBootPart> {}

/*
 * IO methods.
 */

export function parsePreBoot(src: any): PreBootModel {
  return {
    userId: src.user_id,
    userIsVisitor: Boolean(src.user_is_visitor),
    companyId: src.company_id,
    companyUrl: src.company_url,
    companyRegion: src.company_region || undefined,
    companyCell: src.company_cell || undefined,
    hasBootAccessibleInboxes: Boolean(src.has_boot_accessible_inboxes),
  };
}

export function parseBoot(src: any): BootModel {
  const inboxes = parseResourceTable(src.inboxes, parseInbox);
  const channels = parseResourceTable(src.channels, parseChannel);
  const signatures = parseResourceTable(src.signatures, parseSignature, (s) => isString(s.body));
  const answers = parseResourceTable(src.canned_answers, parseAnswer, (a) => isString(a.body));
  const answerFolders = parseResourceTable(src.canned_answer_folders, parseAnswerFolder, (answerFolder) =>
    isString(answerFolder.name),
  );

  return {
    access: src.user && parseAccess(src.user),
    preferences: src.user && parsePreferences(src.user),
    userMetadata: src.user && parseUserMetadata(src.user),
    counters: src.counters && parseCountersFromBoot(src, src.received_delegations),
    company: src.company && parseCompany(src.company),
    userId: src.user && src.user.id,
    fetchedAt: src.ts,
    teams: src.teams && src.teams.map(parseTeam),
    teammates: src.team && src.team.map(parseTeammate),
    receivedDelegations: src.received_delegations && src.received_delegations.map(parseDelegation),
    roles: src.roles && src.roles.map(parseRole),
    jobs: parseJobs(src.running_jobs),
    inboxes,
    channels,
    answers,
    answerFolders,
    signatures,
    integrations: parseBootIntegrations(src.integrations),
    legacyApplications: (src.applications || []).map(parseLegacyApplication),
    customViews: parseCustomViews(src.custom_views, src.received_delegations),
    customFields: (src.custom_fields || []).map(parseCustomField),
    calendarVersions: (src.calendar_versions || []).map(parseCalendarVersion),
    calendars: (src.calendar_versions || []).map(parseCalendarFromCalendarVersion),
    plugins: (src.custom_plugins || []).map(parsePlugin),
    contacts: src.channels && parseContacts(src.channels),
    tags: parseTags(src.custom_views_tags),
    lenses: parseLenses(src.lenses),
    pushConfig: src.push && parsePushConfigModel(src.push),
    chatPushConfig: src.chatPush && parsePusherConfigModel(src.chatPush),
    helpChatConfig: src.front_chat && parseHelpChatConfigModel(src.front_chat),
    host: src.vhost,
    buildVersion: src.build_version || 'unknown',
    launchdarkly: src.launchdarkly && parseLaunchdarklyConfig(src.launchdarkly),
    isImpersonating: src.impersonate || false,
    hasTrialRestarted: src.trial_restarted || false,
    allowedDomainForEmbed: src.allow_embed || undefined,
    conversationDataGroup:
      (src.conversation && parseDataGroupFromConversation(src.conversation)) || undefined,
    vacationResponders:
      (src.vacation_responders && src.vacation_responders.map(parseVacationResponder)) || [],
    workspace: parseWorkspace(src.workspace),
  };
}

function parseJobs(jobs: any) {
  return _(jobs).map(tryParseJob).compact().value();
}

function parseResourceTable<T>(collection: any, parser: (src: any) => T, predicate?: (src: any) => boolean) {
  if (!Array.isArray(collection)) {
    return {
      all: [],
      deletedIds: [],
    };
  }

  const all = _(collection)
    .filter((s) => isUndefined(s.deleted_at) && (!predicate || predicate(s)))
    .map(parser)
    .value();

  const deletedIds = collection.filter((r) => r.deleted_at).map((r) => r.id) as ReadonlyArray<number>;

  return {all, deletedIds};
}

function parseCustomViews(customViews: any, receivedDelegations: any) {
  const delegationCustomViews = _(receivedDelegations)
    .map((d) => d.custom_views)
    .compact()
    .flatten()
    .value();

  return _(delegationCustomViews).concat(customViews).compact().map(parseCustomView).value();
}

function parseContacts(channels: any) {
  return _(channels).map(parseContactsFromChannel).flatten().value();
}

function parseTags(tags: any) {
  const tagsData = map(tags, (i: any) => parseDataGroupFromTag(i));
  return mergeDataGroups(...tagsData).tags || [];
}

function parseLenses(lenses: any) {
  const lensesData = map(lenses, (lensSrc: any) => parseDataGroupFromLens(lensSrc));
  return mergeDataGroups(...lensesData).lenses || [];
}

function parseTeammatesBootPart(src: any): TeammatesBootPartModel {
  return {
    fetchedAt: src.ts,
    teammates: src.team,
    total: src.total,
    checkpoint: src.checkpoint,
  };
}

function parseInboxesAndChannelsBootPart(src: any): InboxesAndChannelsBootPartModel {
  return {
    fetchedAt: src.ts,
    inboxes: src.inboxes,
    channels: src.channels,
    total: src.total,
    checkpoint: src.checkpoint,
  };
}

/*
 * API type.
 */

export const ApiPreBoot = makeApiType('preBoot', RPreBoot, parsePreBoot);
export const ApiBoot = makeApiType('boot', RBoot, parseBoot);
export const ApiTeammatesBootPart = makeApiType(
  'teammatesBootPart',
  RTeammatesBootPart,
  parseTeammatesBootPart,
);
export const ApiInboxesAndChannelsBootPart = makeApiType(
  'inboxesAndChannelsBootPart',
  RInboxesAndChannelsBootPart,
  parseInboxesAndChannelsBootPart,
);
